import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { notify, NOTIFY_STATUS, TYPE_GROUPS, type NotifyStatus } from "@/app/notify";
import { ClearFilters } from "@/app/ClearFilters";
import { Pager } from "@/app/Pager";
import { loadingOf } from "@/app/queries";
import { totalOf, useCursorPage } from "@/app/usePage";
import { useRange } from "@/app/useRange";
import { Alert, Button, DateRange, EmptyState, FILTER_W, formatTime, Select, Table, TableWrap, Th, Toolbar } from "@/ui";
import { NotifyEventDetail } from "./NotifyEventDetail";
import { LevelPill, StatusPill } from "./NotifyPills";

/** 缺省看最近 90 天，即事件表的保留期（docs/03 第 18 节）。 */
const DEFAULT_RANGE = { preset: "90d" } as const;
/** 类型前缀按字面匹配（docs/04 第 15 节），带点以免 account 匹配到 account_x。 */
const TYPE_OPTIONS = [{ value: "", label: "全部类型" }, ...TYPE_GROUPS.map(([p, label]) => ({ value: `${p}.`, label: `${label}（${p}.）` }))];
const STATUS_OPTIONS = [{ value: "", label: "全部状态" }, ...(Object.keys(NOTIFY_STATUS) as NotifyStatus[]).map((s) => ({ value: s, label: NOTIFY_STATUS[s].label }))];

/** “发送记录”页签：按 id 倒序游标翻页、带总数（D182），按类型前缀、状态与时间范围（北京时间选择、换成 UTC 的 RFC 3339）筛选；点详情看实际发出的报文，failed 与 skipped 可重新发送。 */
export function NotifyEvents() {
  const { range, setRange, span, refresh } = useRange(DEFAULT_RANGE);
  const [type, setType] = useState("");
  const [status, setStatus] = useState("");
  const [open, setOpen] = useState<number | null>(null);
  const from = span?.from.toISOString() ?? "";
  // 预设范围（近 N 天）不传 to：接口按 [from, to) 过滤，to 固定在选定时刻，轮询就永远看不到之后的新事件；自定义范围照旧传 from、to
  const to = span && !("preset" in range) ? span.to.toISOString() : "";
  const filter = { type, status, from, to };
  const pager = useCursorPage<number>("notify-events", filter);
  // 状态会随投递变化，列表十秒刷新一次；总数同样轮询，但键里没有翻页参数，翻页不重取
  const list = useQuery({ queryKey: ["notify-events", filter, pager.limit, pager.cursor], queryFn: () => notify.events({ ...filter, limit: pager.limit, before_id: pager.cursor }), enabled: !!span, placeholderData: keepPreviousData, refetchInterval: 10_000 });
  const total = useQuery({ queryKey: ["notify-events", "count", filter], queryFn: () => notify.eventCount(filter), enabled: !!span, refetchInterval: 10_000 }); // 总数随新事件一起轮询（D182）
  const paged = { ...pager.view(list.data ?? [], (r) => r.id), ...totalOf(total.data) };
  const filtered = !!type || !!status || !("preset" in range && range.preset === DEFAULT_RANGE.preset);
  const pick = (set: (v: string) => void) => (v: string) => { set(v); refresh(); };
  return (
    <>
      {list.error && <Alert tone="danger" title="加载失败">{list.error.message}</Alert>}
      <Toolbar actions={filtered && <ClearFilters onClear={() => { setType(""); setStatus(""); setRange(DEFAULT_RANGE); }} />}>
        <DateRange size="sm" value={range} onChange={setRange} />
        <Select size="sm" width={FILTER_W.select} ariaLabel="事件类型" options={TYPE_OPTIONS} value={type} onChange={pick(setType)} />
        <Select size="sm" width={FILTER_W.select} ariaLabel="状态" options={STATUS_OPTIONS} value={status} onChange={pick(setStatus)} />
      </Toolbar>
      <TableWrap loading={loadingOf(list)}>
        {paged.rows.length ? (
          <>
            <Table dense>
              <thead><tr><Th w="time">时间</Th><Th w={200}>类型</Th><Th w={80}>等级</Th><Th w="wide">标题</Th><Th w="status">状态</Th><Th w="num" numeric>次数</Th><Th w="num" numeric>响应码</Th><Th /></tr></thead>
              <tbody>
                {paged.rows.map((e) => (
                  <tr key={e.id}>
                    <td className="ax-mono">{formatTime(e.occurred_at)}</td><td className="ax-mono">{e.type}</td><td><LevelPill level={e.level} /></td><td>{e.title}</td>
                    <td><StatusPill status={e.status} /></td><td className="ax-num">{e.attempts}</td><td className="ax-num">{e.last_status_code ?? "—"}</td>
                    <td><Button variant="ghost" size="sm" aria-label={`查看 ${e.title} 的详情`} onClick={() => setOpen(e.id)}>详情</Button></td>
                  </tr>
                ))}
              </tbody>
            </Table>
            <Pager {...paged} />
          </>
        ) : (
          !list.isPending && <EmptyState title={filtered ? "没有符合条件的记录" : "还没有事件"}>{filtered ? "" : "后台操作与系统事件发生后会记在这里。"}</EmptyState>
        )}
      </TableWrap>
      {open !== null && <NotifyEventDetail id={open} onClose={() => setOpen(null)} />}
    </>
  );
}
